import cn from 'classnames';
import * as R from 'ramda';
import { ReactElement, useCallback, useMemo } from 'react';
import { CommerceOpsTable } from './CommerceOpsTable';
import {
  AsinDimension,
  KEYWORD_PERFORMANCE_DIMENSION_CONFIG_MAP,
  KEYWORD_SESSIONS_BY_ASIN_DIMENSION_CONFIG_MAP,
  KeywordPerformanceDimension,
  KeywordTabProps
} from './CommerceOpsUtils';
import { KeywordSearchTrends } from './KeywordSearchTrends';

const Keyword = ({
  data: { keywordTrends, keywordPerformance, sessionsByAsin },
  downloadTable
}: KeywordTabProps): ReactElement => {
  const performanceDataMap = useMemo(
    () => ({
      [KeywordPerformanceDimension.KEYWORD]: keywordPerformance
    }),
    [keywordPerformance]
  );

  const sessionsDataMap = useMemo(
    () => ({
      [AsinDimension.ASIN]: sessionsByAsin
    }),
    [sessionsByAsin]
  );

  const showKeywordSearchTrends = useMemo(
    () => Object.values(keywordTrends).some(entryArr => !R.isEmpty(entryArr)),
    [keywordTrends]
  );
  const showKeywordPerformance = useMemo(
    () => !R.isEmpty(keywordPerformance),
    [keywordPerformance]
  );
  // Alone on the page the sessions table fills it, like the Buybox table. Below the chart and
  // with no performance table it takes its full height instead, so the page is the only scroller;
  // a table capped at the leftover viewport height would have no room for rows.
  const sessionsOnly = !showKeywordSearchTrends && !showKeywordPerformance;
  const sessionsVhMax = showKeywordPerformance ? 40 : null;

  const downloadPerformanceCsv = useCallback(
    () => downloadTable('performance', performanceDataMap),
    [performanceDataMap, downloadTable]
  );

  const downloadSessionsCsv = useCallback(
    () => downloadTable('sessions', sessionsDataMap),
    [sessionsDataMap, downloadTable]
  );

  return (
    <>
      {showKeywordSearchTrends && (
        <div className='commerce-ops-section keyword-trends-section'>
          <KeywordSearchTrends data={keywordTrends} downloadTable={downloadTable} />
        </div>
      )}
      {showKeywordPerformance && (
        <div className='commerce-ops-section'>
          <CommerceOpsTable
            topRowConfig={{ showTopRow: true, dropdownEnabled: false }}
            dimensionConfigMap={KEYWORD_PERFORMANCE_DIMENSION_CONFIG_MAP}
            dataMap={performanceDataMap}
            onDownload={downloadPerformanceCsv}
          />
        </div>
      )}
      <div className={cn('commerce-ops-section', { 'fill-section': sessionsOnly })}>
        <CommerceOpsTable
          topRowConfig={{ showTopRow: true, dropdownEnabled: false }}
          dimensionConfigMap={KEYWORD_SESSIONS_BY_ASIN_DIMENSION_CONFIG_MAP}
          dataMap={sessionsDataMap}
          onDownload={downloadSessionsCsv}
          viewConfig={{ componentVhMax: sessionsVhMax, fillHeight: sessionsOnly }}
        />
      </div>
    </>
  );
};

export { Keyword };
